import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import Joyride, { Step, CallBackProps, STATUS, Placement } from "react-joyride";

interface TourStep extends Omit<Step, 'target'> {
  target: string;
  requiredPlan?: "starter" | "pro" | "enterprise";
}

interface TourDefinitions {
  [key: string]: TourStep[];
}

// ========== ERWEITERTE TOUR-INHALTE MIT JSX ==========
const TOURS: TourDefinitions = {
  // ========== WELCOME TOUR (Basis für alle User) ==========
  welcome: [
    {
      target: '[data-tour="dashboard"]',
      content: (
        <div className="space-y-2">
          <p className="font-semibold text-base">Willkommen bei CoreGrowth! 👋</p>
          <p>Dies ist Ihr Dashboard – Ihre Kommandozentrale für strategische Preisentscheidungen.</p>
          <p className="text-muted-foreground text-sm">Hier sehen Sie auf einen Blick:</p>
          <ul className="list-disc pl-4 text-sm space-y-1 text-muted-foreground">
            <li>Aktuelle Szenarien und deren Status</li>
            <li>Wichtige Alerts (bei Pro+)</li>
            <li>Ihren Fortschritt beim Onboarding</li>
          </ul>
        </div>
      ),
      disableBeacon: true,
      placement: "center" as Placement,
    },
    {
      target: '[data-tour="quick-actions"]',
      content: (
        <div className="space-y-2">
          <p className="font-semibold">Schnellaktionen – Ihr Startpunkt</p>
          <p className="text-sm">Hier starten Sie die wichtigsten Aktionen:</p>
          <ul className="list-disc pl-4 text-sm space-y-1">
            <li><strong>Neues Szenario:</strong> Preisänderung simulieren</li>
            <li><strong>Daten importieren:</strong> Ihre Daten hochladen</li>
          </ul>
          <p className="text-xs text-primary mt-2">💡 Tipp: Starten Sie mit einem einfachen Szenario!</p>
        </div>
      ),
      placement: "bottom" as Placement,
    },
    {
      target: '[data-tour="onboarding-checklist"]',
      content: (
        <div className="space-y-2">
          <p className="font-semibold">Ihre Fortschritts-Checkliste</p>
          <p className="text-sm">Diese Liste zeigt, welche Schritte noch offen sind:</p>
          <ul className="list-disc pl-4 text-sm space-y-1">
            <li>Firmendaten vervollständigen</li>
            <li>Erste Produktgruppe anlegen</li>
            <li>Erstes Szenario erstellen</li>
          </ul>
          <p className="text-xs text-muted-foreground mt-2">Arbeiten Sie die Punkte ab, um CoreGrowth optimal zu nutzen.</p>
        </div>
      ),
      placement: "left" as Placement,
    },
    {
      target: '[data-tour="scenarios"]',
      content: (
        <div className="space-y-2">
          <p className="font-semibold">Szenarien – Das Herzstück</p>
          <p className="text-sm">Szenarien sind simulierte Preisänderungen. Sie können:</p>
          <ul className="list-disc pl-4 text-sm space-y-1">
            <li>Verschiedene Preisstrategien testen</li>
            <li>Auswirkungen auf Umsatz und Marge sehen</li>
            <li>Szenarien vergleichen und freigeben</li>
          </ul>
          <p className="text-xs text-primary mt-2">💡 Jede Preisänderung sollte zuerst simuliert werden!</p>
        </div>
      ),
      placement: "right" as Placement,
    },
    {
      target: '[data-tour="stammdaten"]',
      content: (
        <div className="space-y-2">
          <p className="font-semibold">Stammdaten – Ihre Datenbasis</p>
          <p className="text-sm">Hier pflegen Sie:</p>
          <ul className="list-disc pl-4 text-sm space-y-1">
            <li><strong>Produktgruppen:</strong> Kategorien für Ihre Produkte</li>
            <li><strong>Kunden:</strong> Mit Segmenten (A/B/C) für Preissensitivität</li>
            <li><strong>Annahmensets:</strong> Elastizitätswerte für Simulationen</li>
          </ul>
          <p className="text-xs text-primary mt-2">💡 Beginnen Sie mit 2-3 Produktgruppen</p>
        </div>
      ),
      placement: "right" as Placement,
    },
    {
      target: '[data-tour="pricing-navigator-button"]',
      content: (
        <div className="space-y-2">
          <p className="font-semibold">Pricing Navigator – Ihr KI-Assistent 🤖</p>
          <p className="text-sm">Fragen Sie den Navigator in natürlicher Sprache:</p>
          <ul className="list-disc pl-4 text-sm space-y-1">
            <li>„Wie kann ich meine Marge verbessern?"</li>
            <li>„Erstelle ein Szenario für 5% Preiserhöhung"</li>
            <li>„Welche Kunden haben die höchste Marge?"</li>
          </ul>
          <p className="text-xs text-muted-foreground mt-2">Verfügbar ab Pro-Plan</p>
        </div>
      ),
      placement: "left" as Placement,
      requiredPlan: "pro",
    },
    {
      target: '[data-tour="cost-drivers"]',
      content: (
        <div className="space-y-2">
          <p className="font-semibold">Kostentreiber überwachen</p>
          <p className="text-sm">Verfolgen Sie externe Kosten:</p>
          <ul className="list-disc pl-4 text-sm space-y-1">
            <li>Rohstoffpreise (Aluminium, Stahl, etc.)</li>
            <li>Energiekosten (Strom, Gas)</li>
            <li>Personalkosten</li>
          </ul>
          <p className="text-xs text-muted-foreground mt-2">Mit automatischer Aktualisierung aus externen Quellen.</p>
        </div>
      ),
      placement: "right" as Placement,
      requiredPlan: "pro",
    },
    {
      target: '[data-tour="alerts"]',
      content: (
        <div className="space-y-2">
          <p className="font-semibold">Alerts – Proaktive Warnungen</p>
          <p className="text-sm">Sie werden automatisch informiert bei:</p>
          <ul className="list-disc pl-4 text-sm space-y-1">
            <li>🔴 Kritischen Margenveränderungen</li>
            <li>🟡 Schwellenwert-Überschreitungen</li>
            <li>ℹ️ Wichtigen Kostenänderungen</li>
          </ul>
        </div>
      ),
      placement: "right" as Placement,
      requiredPlan: "pro",
    },
    {
      target: '[data-tour="benchmarks"]',
      content: (
        <div className="space-y-2">
          <p className="font-semibold">Benchmarks – Branchenvergleich</p>
          <p className="text-sm">Vergleichen Sie Ihre Performance mit anonymisierten Branchendaten:</p>
          <ul className="list-disc pl-4 text-sm space-y-1">
            <li>Margen-Vergleich</li>
            <li>Preisniveau-Analyse</li>
            <li>Best-Practice-Insights</li>
          </ul>
        </div>
      ),
      placement: "right" as Placement,
      requiredPlan: "pro",
    },
    {
      target: '[data-tour="optimization"]',
      content: (
        <div className="space-y-2">
          <p className="font-semibold">KI-Optimierung – Beste Strategie finden</p>
          <p className="text-sm">Die KI testet tausende Kombinationen:</p>
          <ul className="list-disc pl-4 text-sm space-y-1">
            <li>Definieren Sie Ihre Ziele (Marge, Umsatz)</li>
            <li>Setzen Sie Einschränkungen</li>
            <li>Erhalten Sie die optimale Preisstrategie</li>
          </ul>
          <p className="text-xs text-muted-foreground mt-2">Enterprise-Feature</p>
        </div>
      ),
      placement: "right" as Placement,
      requiredPlan: "enterprise",
    },
    {
      target: '[data-tour="sidebar-toggle"]',
      content: (
        <div className="space-y-2">
          <p className="font-semibold">Navigation anpassen</p>
          <p className="text-sm">Die Sidebar können Sie jederzeit ein- und ausklappen.</p>
          <p className="text-base mt-3">🎉 <strong>Viel Erfolg mit CoreGrowth!</strong></p>
          <p className="text-xs text-muted-foreground">Sie können diese Tour jederzeit unter Hilfe erneut starten.</p>
        </div>
      ),
      placement: "right" as Placement,
    },
  ],

  // ========== FIRST SCENARIO TOUR ==========
  firstScenario: [
    {
      target: '[data-tour="scenario-tabs"]',
      content: (
        <div className="space-y-2">
          <p className="font-semibold">Szenario-Erstellung starten</p>
          <p className="text-sm">Sie haben zwei Möglichkeiten:</p>
          <ul className="list-disc pl-4 text-sm space-y-1">
            <li><strong>KI-Assistent:</strong> Beschreiben Sie, was Sie testen möchten</li>
            <li><strong>Manuell:</strong> Wählen Sie Strategie und Parameter selbst</li>
          </ul>
          <p className="text-xs text-muted-foreground mt-2">Der KI-Assistent ist ab Pro verfügbar.</p>
        </div>
      ),
      disableBeacon: true,
      placement: "bottom" as Placement,
    },
    {
      target: '[data-tour="strategy-cards"]',
      content: (
        <div className="space-y-2">
          <p className="font-semibold">Strategie wählen</p>
          <p className="text-sm">Drei bewährte Ansätze:</p>
          <ul className="list-disc pl-4 text-sm space-y-1">
            <li><strong>Preise erhöhen:</strong> Direkte Margensteigerung</li>
            <li><strong>Rabatte reduzieren:</strong> Weniger Nachlass bei Verhandlungen</li>
            <li><strong>Mehrwert statt Rabatt:</strong> Zusatzleistungen statt Preisnachlass</li>
          </ul>
        </div>
      ),
      placement: "bottom" as Placement,
    },
    {
      target: '[data-tour="scope-selection"]',
      content: (
        <div className="space-y-2">
          <p className="font-semibold">Scope definieren</p>
          <p className="text-sm">Bestimmen Sie, wo die Änderung gelten soll:</p>
          <ul className="list-disc pl-4 text-sm space-y-1">
            <li><strong>Kundensegment:</strong> A, B oder C-Kunden</li>
            <li><strong>Produktgruppe:</strong> Welche Kategorien?</li>
          </ul>
          <p className="text-xs text-primary mt-2">💡 Beginnen Sie fokussiert, z.B. nur Premium-Kunden</p>
        </div>
      ),
      placement: "top" as Placement,
    },
    {
      target: '[data-tour="assumption-set"]',
      content: (
        <div className="space-y-2">
          <p className="font-semibold">Annahmenset wählen</p>
          <p className="text-sm">Das Set definiert die <strong>Preiselastizität</strong> – also wie stark Kunden auf Preisänderungen reagieren.</p>
          <ul className="list-disc pl-4 text-sm space-y-1">
            <li><strong>CoreGrowth-Sets:</strong> Basieren auf Branchendaten</li>
            <li><strong>Eigene Sets:</strong> Individuelle Elastizitäten</li>
          </ul>
        </div>
      ),
      placement: "top" as Placement,
    },
    {
      target: '[data-tour="simulate-button"]',
      content: (
        <div className="space-y-2">
          <p className="font-semibold">Szenario erstellen</p>
          <p className="text-sm">Klicken Sie auf „Erstellen", um das Szenario anzulegen.</p>
          <p className="text-sm">Danach können Sie:</p>
          <ul className="list-disc pl-4 text-sm space-y-1">
            <li>Die Simulation starten</li>
            <li>Ergebnisse analysieren</li>
            <li>Mit anderen Szenarien vergleichen</li>
          </ul>
        </div>
      ),
      placement: "top" as Placement,
    },
  ],

  // ========== STAMMDATEN TOUR ==========
  stammdaten: [
    {
      target: '[data-tour="stammdaten-tabs"]',
      content: (
        <div className="space-y-2">
          <p className="font-semibold">Stammdaten-Übersicht</p>
          <p className="text-sm">Die Tabs organisieren Ihre Daten:</p>
          <ul className="list-disc pl-4 text-sm space-y-1">
            <li><strong>Produktgruppen:</strong> Kategorien für Preisstrategien</li>
            <li><strong>Produkte:</strong> Einzelne Artikel mit Kosten</li>
            <li><strong>Kunden:</strong> Mit A/B/C-Segmentierung</li>
            <li><strong>Annahmen:</strong> Elastizitätswerte</li>
          </ul>
        </div>
      ),
      disableBeacon: true,
      placement: "bottom" as Placement,
    },
    {
      target: '[data-tour="product-groups"]',
      content: (
        <div className="space-y-2">
          <p className="font-semibold">Produktgruppen anlegen</p>
          <p className="text-sm">Gruppieren Sie ähnliche Produkte, um Preisstrategien zu differenzieren.</p>
          <p className="text-xs text-primary mt-2">💡 Beispiel: „Premium-Linie", „Standard", „Economy"</p>
        </div>
      ),
      placement: "bottom" as Placement,
    },
    {
      target: '[data-tour="assumptions"]',
      content: (
        <div className="space-y-2">
          <p className="font-semibold">Elastizitäts-Annahmen</p>
          <p className="text-sm"><strong>Preiselastizität</strong> definiert, wie stark Kunden auf Preisänderungen reagieren.</p>
          <p className="text-sm">Beispiel: -1.0 bedeutet: 10% Preiserhöhung → 10% weniger Absatz</p>
          <p className="text-xs text-primary mt-2">💡 CoreGrowth-Sets basieren auf Branchendaten und sind ein guter Start.</p>
        </div>
      ),
      placement: "bottom" as Placement,
    },
  ],

  // ========== COST DRIVERS TOUR (Pro+) ==========
  costDrivers: [
    {
      target: '[data-tour="cost-driver-list"]',
      content: "Hier verwalten Sie Kostentreiber wie Rohstoffpreise, Energiekosten oder Löhne.",
      disableBeacon: true,
      placement: "bottom" as Placement,
      requiredPlan: "pro",
    },
    {
      target: '[data-tour="cost-driver-add"]',
      content: "Erstellen Sie neue Kostentreiber und verknüpfen Sie sie mit externen Datenquellen.",
      placement: "left" as Placement,
      requiredPlan: "pro",
    },
    {
      target: '[data-tour="cost-driver-trend"]',
      content: "Verfolgen Sie Trends über Zeit und verstehen Sie, wie sich Kosten entwickeln.",
      placement: "top" as Placement,
      requiredPlan: "pro",
    },
  ],

  // ========== ALERTS TOUR (Pro+) ==========
  alerts: [
    {
      target: '[data-tour="alerts-list"]',
      content: "Aktive Alerts zeigen wichtige Ereignisse: Margenveränderungen, Schwellenwert-Überschreitungen und mehr.",
      disableBeacon: true,
      placement: "bottom" as Placement,
      requiredPlan: "pro",
    },
    {
      target: '[data-tour="alerts-config"]',
      content: "Konfigurieren Sie, welche Alerts Sie erhalten möchten und wie Sie benachrichtigt werden.",
      placement: "left" as Placement,
      requiredPlan: "pro",
    },
  ],

  // ========== PRICING NAVIGATOR TOUR (Pro+) ==========
  navigator: [
    {
      target: '[data-tour="navigator-input"]',
      content: "Stellen Sie Fragen in natürlicher Sprache: 'Wie kann ich meine Marge verbessern?' oder 'Erstelle ein Szenario für 5% Preiserhöhung'.",
      disableBeacon: true,
      placement: "top" as Placement,
      requiredPlan: "pro",
    },
    {
      target: '[data-tour="navigator-suggestions"]',
      content: "Vorschläge helfen Ihnen, die richtigen Fragen zu stellen. Klicken Sie einfach darauf.",
      placement: "top" as Placement,
      requiredPlan: "pro",
    },
    {
      target: '[data-tour="navigator-history"]',
      content: "Ihre Konversationen werden gespeichert. Sie können jederzeit frühere Gespräche fortsetzen.",
      placement: "right" as Placement,
      requiredPlan: "pro",
    },
  ],

  // ========== BENCHMARKS TOUR (Pro+) ==========
  benchmarks: [
    {
      target: '[data-tour="benchmark-overview"]',
      content: "Vergleichen Sie Ihre KPIs mit anonymisierten Branchendaten. Sehen Sie, wo Sie stehen.",
      disableBeacon: true,
      placement: "bottom" as Placement,
      requiredPlan: "pro",
    },
    {
      target: '[data-tour="benchmark-optin"]',
      content: "Nehmen Sie am Benchmark-Programm teil, um noch genauere Vergleichsdaten zu erhalten.",
      placement: "bottom" as Placement,
      requiredPlan: "pro",
    },
  ],

  // ========== OPTIMIZATION TOUR (Enterprise) ==========
  optimization: [
    {
      target: '[data-tour="optimization-objectives"]',
      content: "Definieren Sie Ihre Ziele: Maximale Marge, Umsatzwachstum oder Marktanteil.",
      disableBeacon: true,
      placement: "bottom" as Placement,
      requiredPlan: "enterprise",
    },
    {
      target: '[data-tour="optimization-constraints"]',
      content: "Setzen Sie Einschränkungen: Maximale Preiserhöhung, Mindest-Volumen etc.",
      placement: "bottom" as Placement,
      requiredPlan: "enterprise",
    },
    {
      target: '[data-tour="optimization-run"]',
      content: "Starten Sie die Optimierung. Die KI testet tausende Kombinationen und findet die beste Strategie.",
      placement: "left" as Placement,
      requiredPlan: "enterprise",
    },
  ],
};

const PLAN_LEVELS: Record<string, number> = {
  starter: 0,
  trial: 1,
  pro: 1,
  enterprise: 2,
};

interface TourContextType {
  startTour: (tourName: string, userPlan?: string) => void;
  completeTour: (tourName: string) => void;
  hasCompletedTour: (tourName: string) => boolean;
  resetTour: (tourName: string) => void;
  resetAllTours: () => void;
  shouldAutoStartWelcomeTour: () => boolean;
}

const TourContext = createContext<TourContextType | undefined>(undefined);

interface TourProviderProps {
  children: ReactNode;
}

export function TourProvider({ children }: TourProviderProps) {
  const [activeTour, setActiveTour] = useState<string | null>(null);
  const [completedTours, setCompletedTours] = useState<string[]>([]);
  const [run, setRun] = useState(false);
  const [filteredSteps, setFilteredSteps] = useState<Step[]>([]);
  const [currentUserPlan, setCurrentUserPlan] = useState<string>("starter");

  useEffect(() => {
    const saved = localStorage.getItem("completedTours");
    if (saved) {
      try {
        setCompletedTours(JSON.parse(saved));
      } catch {
        setCompletedTours([]);
      }
    }
  }, []);

  const filterStepsByPlan = (steps: TourStep[], userPlan: string): Step[] => {
    const userPlanLevel = PLAN_LEVELS[userPlan] ?? 0;
    
    return steps.filter(step => {
      if (!step.requiredPlan) return true;
      const requiredLevel = PLAN_LEVELS[step.requiredPlan] ?? 0;
      return userPlanLevel >= requiredLevel;
    }).map(({ requiredPlan, ...rest }) => ({
      ...rest,
      disableBeacon: true, // Always disable beacon for all steps
    } as Step));
  };

  const startTour = (tourName: string, userPlan: string = "starter") => {
    if (TOURS[tourName]) {
      setCurrentUserPlan(userPlan);
      const steps = filterStepsByPlan(TOURS[tourName], userPlan);
      
      // Sicherheitscheck: Nur starten wenn mindestens ein Target-Element im DOM existiert
      const hasValidTarget = steps.some(step => {
        try {
          return document.querySelector(step.target as string) !== null;
        } catch {
          return false;
        }
      });
      
      if (!hasValidTarget) {
        console.warn(`[TourContext] No valid DOM targets found for tour: ${tourName}, skipping`);
        return;
      }
      
      setFilteredSteps(steps);
      setActiveTour(tourName);
      setRun(true);
    }
  };

  const completeTour = (tourName: string) => {
    if (!completedTours.includes(tourName)) {
      const updated = [...completedTours, tourName];
      setCompletedTours(updated);
      localStorage.setItem("completedTours", JSON.stringify(updated));
    }
    setActiveTour(null);
    setRun(false);
    setFilteredSteps([]);
  };

  const hasCompletedTour = (tourName: string) => completedTours.includes(tourName);

  const resetTour = (tourName: string) => {
    const updated = completedTours.filter((t) => t !== tourName);
    setCompletedTours(updated);
    localStorage.setItem("completedTours", JSON.stringify(updated));
  };

  const resetAllTours = () => {
    setCompletedTours([]);
    localStorage.removeItem("completedTours");
  };

  const shouldAutoStartWelcomeTour = () => {
    // Check if user has never seen any tour and this is their first session
    const hasSeenWelcome = completedTours.includes("welcome");
    const isFirstSession = !localStorage.getItem("hasVisitedDashboard");
    return !hasSeenWelcome && isFirstSession;
  };

  const handleCallback = (data: CallBackProps) => {
    const { status } = data;
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      if (activeTour) {
        completeTour(activeTour);
      }
    }
  };

  return (
    <TourContext.Provider
      value={{
        startTour,
        completeTour,
        hasCompletedTour,
        resetTour,
        resetAllTours,
        shouldAutoStartWelcomeTour,
      }}
    >
      {children}
      <Joyride
        steps={filteredSteps}
        run={run}
        continuous
        showProgress
        showSkipButton
        scrollToFirstStep
        spotlightClicks
        disableOverlayClose={false}
        callback={handleCallback}
        styles={{
          options: {
            primaryColor: "hsl(217, 91%, 60%)",
            zIndex: 10000,
            arrowColor: "hsl(var(--background))",
            backgroundColor: "hsl(var(--background))",
            textColor: "hsl(var(--foreground))",
          },
          tooltip: {
            borderRadius: 8,
            padding: 16,
          },
          buttonNext: {
            borderRadius: 6,
            padding: "8px 16px",
          },
          buttonBack: {
            borderRadius: 6,
            marginRight: 8,
          },
          buttonSkip: {
            color: "hsl(var(--muted-foreground))",
          },
          spotlight: {
            borderRadius: 8,
          },
        }}
        locale={{
          back: "Zurück",
          close: "Schließen",
          last: "Fertig",
          next: "Weiter",
          skip: "Überspringen",
        }}
        floaterProps={{
          disableAnimation: true,
        }}
      />
    </TourContext.Provider>
  );
}

export function useTour() {
  const context = useContext(TourContext);
  if (!context) {
    // Safe defaults statt Error werfen
    console.warn("[useTour] Used outside TourProvider, returning safe defaults");
    return {
      startTour: () => {},
      completeTour: () => {},
      hasCompletedTour: () => false,
      resetTour: () => {},
      resetAllTours: () => {},
      shouldAutoStartWelcomeTour: () => false,
    };
  }
  return context;
}
